import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  FormLabel,
  CircularProgress,
  Alert,
  Stack,
  FormHelperText,
  Chip,
  Card,
  CardContent,
  Tooltip,
  IconButton,
  Collapse,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { useTheme, alpha } from "@mui/material/styles";
import {
  CloudUpload as CloudUploadIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Assignment as AssignmentIcon,
  Attachment as AttachmentIcon,
  HelpOutline as HelpOutlineIcon,
} from "@mui/icons-material";
import useFormData from "@/hooks/useFormData";
import useApiErrorHandler from "@/hooks/useApiErrorHandler";
import useGetMetadata from "@/hooks/useGetMetaData";
import { getSingleSelectStyles } from "@/lib/styles";
import StepInputsForm from "./firstStepInputsForm";
import useAuthenticatedQuery from "@/hooks/useAuthenticatedQuery";
import { useSession } from "@/hooks/useSession";
// import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import ObservationGuideDialog from "@/components/dialogs/ObservationGuideDialog";

const defaultFormData = {
  description: "",
  site: null,
  workflow: null,
  priority: null,
  workPermitId: "",
  attachments: [],
  stepInputsData: {},
};

const ObservationForm = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const singleSelectStyles = getSingleSelectStyles(theme);
  const { session } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: Sites,
    isLoading: sitesLoading,
    error: sitesError,
  } = useAuthenticatedQuery(["sites"], `/api/v1/metadata/sites`, {
    enabled: !!session,
  });

  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      bgcolor: theme.palette.checklist.surface.primary,
      "& fieldset": {
        borderColor: theme.palette.checklist.border.subtle,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.checklist.border.focus,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-error fieldset": {
        borderColor: theme.palette.error.main,
      },
    },
  };

  const { postData } = useFormData();
  const { toast, setToast, handleToastClose } = useApiErrorHandler();
  const {
    WorkFlows = [],
    UsersList = [],
    priorityList = [],
    isLoading: metadataLoading,
  } = useGetMetadata() || {};

  const [isFormValid, setIsFormValid] = useState(false);
  const [formData, setFormData] = useState(defaultFormData);
  const [dynamicFields, setDynamicFields] = useState([]);
  const [inputValues, setInputValues] = useState({});
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileInputValues, setFileInputValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [formProgress, setFormProgress] = useState(0);
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [errors, setErrors] = useState({
    description: false,
    site: false,
    workflow: false,
    priority: false,
  });
  const [touched, setTouched] = useState({
    description: false,
    site: false,
    workflow: false,
    priority: false,
  });

  const SitesList = useMemo(
    () =>
      Sites?.map(({ id, name }) => ({
        value: id,
        label: name,
      })) || [],
    [Sites]
  );

  const observationWorkflowOptions = useMemo(
    () =>
      WorkFlows.filter((wf) => wf.target === "OBSERVATION").map((wf) => ({
        value: wf.id,
        label: wf.name,
      })),
    [WorkFlows]
  );

  // Calculate form completion progress
  useEffect(() => {
    const requiredFields = ["workflow", "site", "priority", "description"];
    const completedFields = requiredFields.filter((field) => {
      if (field === "description") return formData[field]?.trim();
      return formData[field]?.value;
    });

    const dynamicRequiredFields = dynamicFields.filter(
      (field) => field.required
    );
    const completedDynamicFields = dynamicRequiredFields.filter((field) => {
      const value = inputValues[field.id];
      if (field.input_type?.toUpperCase() === "FILE_UPLOAD") {
        return fileInputValues[field.id]?.length > 0;
      }
      return value !== undefined && value !== null && value !== "";
    });

    const totalRequired = requiredFields.length + dynamicRequiredFields.length;
    const totalCompleted =
      completedFields.length + completedDynamicFields.length;

    setFormProgress(
      totalRequired > 0 ? (totalCompleted / totalRequired) * 100 : 0
    );
  }, [formData, dynamicFields, inputValues, fileInputValues]);

  useEffect(() => {
    if (!formData.workflow?.value || !WorkFlows.length) {
      if (dynamicFields.length !== 0) {
        setDynamicFields([]);
        setInputValues({});
      }
      return;
    }

    const workflow = WorkFlows.find((wf) => wf.id === formData.workflow.value);
    const firstStep = workflow?.steps?.find((step) => step.parent_id === null);
    const inputs = firstStep?.step_inputs || [];

    const inputsChanged =
      inputs.length !== dynamicFields.length ||
      inputs.some((input, i) => input.id !== dynamicFields[i]?.id);

    if (inputsChanged) {
      setDynamicFields(inputs);
      setInputValues({});
    }
  }, [formData.workflow?.value, WorkFlows, dynamicFields]);

  const validateField = useCallback((name, value) => {
    if (name === "description") {
      return !value?.trim();
    }
    return !value;
  }, []);

  const getFieldError = useCallback(
    (name, value) => {
      if (name === "description") {
        if (!value?.trim())
          return t("observationForm.errorDescriptionRequired");
        if (value.trim().length < 10)
          return t("observationForm.errorDescriptionTooShort");
        return "";
      }
      if (!value) {
        const fieldLabels = {
          workflow: t("observationForm.observationTypeLabel"),
          site: t("observationForm.siteLabel"),
          priority: t("observationForm.priorityLabel"),
        };
        return t("observationForm.errorRequired", { field: fieldLabels[name] });
      }
      return "";
    },
    [t]
  );

  const validateForm = useCallback(
    (
      description,
      siteValue,
      workflowValue,
      priorityValue,
      inputValues,
      dynamicFields,
      fileInputValues
    ) => {
      const isValid =
        !!description?.trim() &&
        description.trim().length >= 10 &&
        !!siteValue &&
        !!workflowValue &&
        !!priorityValue &&
        !dynamicFields.some((field) => {
          const value = inputValues[field.id];
          const type = field.input_type?.toUpperCase();

          // CONFIRMATION is always required (must be checked)
          if (type === "CONFIRMATION") {
            return value !== true;
          }

          // For other types, check if field is required
          if (!field.required) return false;

          if (type === "BOOLEAN") {
            return value !== "YES" && value !== "NO";
          }
          if (type === "FILE_UPLOAD") {
            return (
              !fileInputValues[field.id] ||
              fileInputValues[field.id].length === 0
            );
          }
          if (type === "MULTI_USER_SELECTION" || type === "MULTI_SELECTION") {
            return !Array.isArray(value) || value.length === 0;
          }
          return (
            value === "" ||
            value === null ||
            value === undefined ||
            (Array.isArray(value) && value.length === 0)
          );
        });

      return isValid;
    },
    []
  );

  useEffect(() => {
    setIsFormValid(
      validateForm(
        formData.description,
        formData.site?.value,
        formData.workflow?.value,
        formData.priority?.value,
        inputValues,
        dynamicFields,
        fileInputValues
      )
    );
  }, [
    formData.description,
    formData.site?.value,
    formData.workflow?.value,
    formData.priority?.value,
    inputValues,
    dynamicFields,
    fileInputValues,
    validateForm,
  ]);

  const handleSingleSelect = useCallback(
    (name) => (e) => {
      setFormData((prev) => ({
        ...prev,
        [name]: e ? { value: e.value, label: e.label } : null,
      }));
      setTouched((prev) => ({ ...prev, [name]: true }));
      setErrors((prev) => ({
        ...prev,
        [name]: !e,
      }));
    },
    []
  );

  const handleTextChange = useCallback(
    (name) => (e) => {
      const value = e.target.value;
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      setTouched((prev) => ({ ...prev, [name]: true }));
      setErrors((prev) => ({
        ...prev,
        [name]: validateField(name, value),
      }));
    },
    [validateField]
  );

  const handleBlur = useCallback(
    (name) => () => {
      setTouched((prev) => ({ ...prev, [name]: true }));
      setErrors((prev) => ({
        ...prev,
        [name]: validateField(name, formData[name]),
      }));
    },
    [formData, validateField]
  );

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      const files = Array.from(e.dataTransfer.files);
      const validFiles = files.filter((file) => {
        const isValidType =
          file.type.startsWith("image/") || file.type === "application/pdf";
        const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB
        return isValidType && isValidSize;
      });

      if (validFiles.length !== files.length) {
        setToast({
          open: true,
          severity: "warning",
          message: t("observationForm.fileValidationWarning"),
        });
      }

      setSelectedFiles((prev) => [...prev, ...validFiles]);
    },
    [setToast, t]
  );

  const handleFileChange = useCallback(
    (e) => {
      const files = Array.from(e.target.files);
      const validFiles = files.filter((file) => {
        const isValidType =
          file.type.startsWith("image/") || file.type === "application/pdf";
        const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB
        return isValidType && isValidSize;
      });

      if (validFiles.length !== files.length) {
        setToast({
          open: true,
          severity: "warning",
          message: t("observationForm.fileValidationWarning"),
        });
      }

      setSelectedFiles((prev) => [...prev, ...validFiles]);
    },
    [setToast, t]
  );

  const removeFile = useCallback((index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleDynamicChange = useCallback((id, value) => {
    setInputValues((prev) => ({
      ...prev,
      [id]: value,
    }));
  }, []);

  const prepareFilledStepInputs = useCallback(() => {
    return dynamicFields
      .map(({ id, input_type }) => {
        let raw_value = inputValues[id];
        let file_handles = (fileInputValues[id] || []).map((f) => f.name);
        const inputTypeUpper = input_type?.toUpperCase();

        if (inputTypeUpper === "TIMESTAMP" && raw_value) {
          raw_value = new Date(raw_value).toISOString();
        }

        // Handle BOOLEAN - convert YES/NO to "true"/"false" strings
        // Always send "true" or "false" (no empty strings)
        if (inputTypeUpper === "BOOLEAN") {
          if (raw_value === "YES") {
            raw_value = "true";
          } else {
            // Default to "false" for NO or any other value
            raw_value = "false";
          }
        }

        // Handle CONFIRMATION - send as "true" or "false" string
        if (inputTypeUpper === "CONFIRMATION") {
          raw_value = raw_value === true ? "true" : "false";
        }

        if (inputTypeUpper === "MULTI_USER_SELECTION") {
          if (Array.isArray(raw_value)) {
            if (raw_value.length === 1) {
              raw_value = raw_value[0];
            } else if (raw_value.length > 1) {
              raw_value = raw_value.join(",");
            } else {
              raw_value = "";
            }
          }
        } else if (inputTypeUpper === "MULTI_SELECTION") {
          raw_value = Array.isArray(raw_value) ? raw_value.join("||") : "";
        } else if (!["BOOLEAN", "CONFIRMATION"].includes(inputTypeUpper)) {
          // Don't convert BOOLEAN or CONFIRMATION to string again
          raw_value = Array.isArray(raw_value)
            ? raw_value.join(",")
            : raw_value?.toString() || "";
        }

        return {
          step_input_id: id,
          file_handles,
          raw_value,
        };
      })
      .filter((item) => {
        // Filter out null items (skipped boolean fields)
        if (item === null) return false;

        const { raw_value, file_handles } = item;
        return (
          (raw_value !== null &&
            raw_value !== undefined &&
            raw_value.length > 0) ||
          (file_handles && file_handles.length > 0)
        );
      });
  }, [dynamicFields, inputValues, fileInputValues]);

  const setInputValuesStable = useCallback((values) => {
    setInputValues(values);
  }, []);

  const setFileInputValuesStable = useCallback((values) => {
    setFileInputValues(values);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!isFormValid) {
      setToast({
        open: true,
        severity: "error",
        message: t("observationForm.errorRequiredFields"),
      });
      setLoading(false);
      return;
    }

    const payload = {
      description: formData.description,
      site_id: formData.site.value,
      workflow_id: formData.workflow.value,
      priority_id: formData.priority.value,
      ...(formData.workPermitId?.trim() && {
        work_permit_id: formData.workPermitId,
      }),
      filled_step_inputs: prepareFilledStepInputs(),
    };

    const form = new FormData();
    form.append(
      "data",
      new Blob([JSON.stringify(payload)], { type: "application/json" })
    );

    selectedFiles.forEach((file) => form.append("attachments", file));

    for (const key in fileInputValues) {
      fileInputValues[key].forEach((file) =>
        form.append("step_input_attachments", file)
      );
    }

    try {
      await postData(form, "/api/v1/observations");

      // Invalidate observations-related queries to ensure fresh data
      await queryClient.invalidateQueries({ queryKey: ["myobservations"] });
      await queryClient.invalidateQueries({ queryKey: ["observations"] });

      setShowSuccess(true);
      setToast({
        open: true,
        severity: "success",
        message: t("observationForm.successMessage"),
      });
    } catch (error) {
      setToast({
        open: true,
        message: t("comment.errorMessage", {
          message: error.response?.data?.message || t("comment.defaultError"),
        }),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!session) {
      navigate("/sign-in", { replace: true });
    }
  }, [session, navigate]);

  if (!session || sitesLoading || metadataLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
        sx={{
          bgcolor: theme.palette.checklist.surface.primary,
          borderRadius: 2,
          p: 4,
        }}
      >
        <CircularProgress
          size={48}
          aria-label={t("observationForm.loadingAria")}
          sx={{ color: theme.palette.primary.main }}
        />
        <Typography variant="body1" color="text.secondary" mt={2}>
          {t("observationForm.loading")}
        </Typography>
      </Box>
    );
  }

  if (sitesError) {
    return (
      <Card
        sx={{
          p: 3,
          bgcolor: theme.palette.error.light,
          borderLeft: `4px solid ${theme.palette.error.main}`,
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" color="error.main" gutterBottom>
          {t("observationForm.errorTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("observationForm.errorMessage")}
        </Typography>
      </Card>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", py: 3 }}>
      {/* Compact Header */}
      <Box sx={{ maxWidth: 1000, mx: "auto", px: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AssignmentIcon sx={{ color: "primary.main", fontSize: 24 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                component="h1"
                sx={{
                  color: "text.primary",
                  fontWeight: 700,
                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                  lineHeight: 1.2,
                }}
              >
                {t("observationForm.title")}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.875rem",
                }}
              >
                {t(
                  "observationForm.subtitle",
                  "Document safety observations and incidents"
                )}
              </Typography>
            </Box>
          </Box>
          <Tooltip title={t("observationForm.helpTooltip")}>
            <IconButton
              onClick={() => setHelpModalOpen(true)}
              size="small"
              sx={{
                color: "primary.main",
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                "&:hover": {
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.2),
                },
              }}
              aria-label={t("observationForm.helpButtonAria")}
            >
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ maxWidth: 1000, mx: "auto", px: { xs: 2, sm: 3 } }}>
        {/* Compact Progress Bar */}
        <Box
          sx={{
            mb: 3,
            p: 2,
            bgcolor: "background.paper",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              {t("observationForm.formProgress")}
            </Typography>
            <Chip
              label={`${Math.round(formProgress)}%`}
              size="small"
              color={formProgress === 100 ? "success" : "primary"}
              sx={{ fontWeight: 600, height: 24 }}
            />
          </Box>
          <Box
            sx={{
              width: "100%",
              height: 6,
              bgcolor: "action.hover",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: `${formProgress}%`,
                height: "100%",
                bgcolor: formProgress === 100 ? "success.main" : "primary.main",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                borderRadius: 3,
              }}
            />
          </Box>
        </Box>

        {showSuccess ? (
          <Card
            elevation={2}
            sx={{
              textAlign: "center",
              py: 6,
              px: 4,
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Success decoration */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                bgcolor: "success.main",
              }}
            />

            <Box
              sx={{
                width: { xs: 64, sm: 80 },
                height: { xs: 64, sm: 80 },
                borderRadius: "50%",
                bgcolor: (theme) => alpha(theme.palette.success.main, 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 3,
                border: "3px solid",
                borderColor: (theme) => alpha(theme.palette.success.main, 0.2),
              }}
            >
              <CheckCircleIcon
                sx={{ fontSize: { xs: 32, sm: 40 }, color: "success.main" }}
              />
            </Box>

            <Typography
              variant="h4"
              sx={{
                color: "text.primary",
                fontWeight: 700,
                mb: 1,
                fontSize: { xs: "1.25rem", sm: "1.5rem", md: "2rem" },
              }}
            >
              {t("observationForm.successTitle")}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                mb: 4,
                fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
                lineHeight: 1.6,
                maxWidth: 500,
                mx: "auto",
              }}
            >
              {t("observationForm.successDescription")}
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={3}
              justifyContent="center"
              sx={{ px: 2 }}
            >
              <Button
                variant="outlined"
                color="primary"
                onClick={() => navigate("/myobservations")}
                sx={{
                  borderRadius: 2,
                  py: { xs: 1.25, sm: 1.5 },
                  px: { xs: 3, sm: 4 },
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: { xs: 150, sm: 200 },
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  borderWidth: 2,
                  "&:hover": {
                    borderWidth: 2,
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                  },
                }}
              >
                {t("observationForm.viewMyObservationsButton")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setFormData(defaultFormData);
                  setDynamicFields([]);
                  setInputValues({});
                  setSelectedFiles([]);
                  setFileInputValues({});
                  setErrors({
                    description: false,
                    site: false,
                    workflow: false,
                    priority: false,
                  });
                  setTouched({
                    description: false,
                    site: false,
                    workflow: false,
                    priority: false,
                  });
                  localStorage.removeItem("step_inputs");
                  setShowSuccess(false);
                }}
                sx={{
                  borderRadius: 2,
                  py: { xs: 1.25, sm: 1.5 },
                  px: { xs: 3, sm: 4 },
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: { xs: 150, sm: 200 },
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  boxShadow: 2,
                  "&:hover": {
                    boxShadow: 4,
                  },
                }}
              >
                {t("observationForm.createNewObservationButton")}
              </Button>
            </Stack>
          </Card>
        ) : (
          <Box component="form" onSubmit={handleSubmit} noValidate>
            {/* Combined Basic Information & Description Section */}
            <Card
              elevation={1}
              sx={{
                mb: 3,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Box
                sx={{
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  px: 2.5,
                  py: 2,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: "primary.main",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    fontSize: "1rem",
                  }}
                >
                  <AssignmentIcon sx={{ mr: 1.5, fontSize: 20 }} />
                  {t("observationForm.basicInfoTitle")}
                </Typography>
              </Box>
              <CardContent sx={{ p: 2.5 }}>
                <Grid container spacing={2.5}>
                  {/* Observation Type */}
                  <Grid size={{ xs: 12 }}>
                    <FormLabel
                      sx={{
                        color: theme.palette.text.primary,
                        mb: 0.75,
                        display: "block",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        "&::after": {
                          content: '"*"',
                          color: theme.palette.error.main,
                          ml: 0.5,
                        },
                      }}
                    >
                      {t("observationForm.observationTypeLabel")}
                    </FormLabel>
                    <Select
                      value={formData.workflow}
                      onChange={handleSingleSelect("workflow")}
                      onBlur={handleBlur("workflow")}
                      options={observationWorkflowOptions}
                      placeholder={t(
                        "observationForm.observationTypePlaceholder"
                      )}
                      styles={{
                        ...singleSelectStyles,
                        menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                        control: (base, state) => ({
                          ...base,
                          minHeight: 38,
                          backgroundColor:
                            theme.palette.checklist.surface.primary,
                          borderColor:
                            errors.workflow && touched.workflow
                              ? theme.palette.error.main
                              : state.isFocused
                                ? theme.palette.primary.main
                                : theme.palette.checklist.border.subtle,
                        }),
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      required
                    />
                    {errors.workflow && touched.workflow && (
                      <FormHelperText error sx={{ mt: 0.5 }}>
                        {getFieldError("workflow", formData.workflow)}
                      </FormHelperText>
                    )}
                  </Grid>

                  {/* Site and Priority */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormLabel
                      sx={{
                        color: theme.palette.text.primary,
                        mb: 0.75,
                        display: "block",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        "&::after": {
                          content: '"*"',
                          color: theme.palette.error.main,
                          ml: 0.5,
                        },
                      }}
                    >
                      {t("observationForm.siteLabel")}
                    </FormLabel>
                    <Select
                      value={formData.site}
                      onChange={handleSingleSelect("site")}
                      onBlur={handleBlur("site")}
                      options={SitesList}
                      placeholder={t("observationForm.sitePlaceholder")}
                      styles={{
                        ...singleSelectStyles,
                        control: (base, state) => ({
                          ...base,
                          minHeight: 38,
                          backgroundColor:
                            theme.palette.checklist.surface.primary,
                          borderColor:
                            errors.site && touched.site
                              ? theme.palette.error.main
                              : state.isFocused
                                ? theme.palette.primary.main
                                : theme.palette.checklist.border.subtle,
                        }),
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      required
                    />
                    {errors.site && touched.site && (
                      <FormHelperText error sx={{ mt: 0.5 }}>
                        {getFieldError("site", formData.site)}
                      </FormHelperText>
                    )}
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormLabel
                      sx={{
                        color: theme.palette.text.primary,
                        mb: 0.75,
                        display: "block",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        "&::after": {
                          content: '"*"',
                          color: theme.palette.error.main,
                          ml: 0.5,
                        },
                      }}
                    >
                      {t("observationForm.priorityLabel")}
                    </FormLabel>
                    <Select
                      value={formData.priority}
                      onChange={handleSingleSelect("priority")}
                      onBlur={handleBlur("priority")}
                      options={priorityList}
                      placeholder={t("observationForm.priorityPlaceholder")}
                      styles={{
                        ...singleSelectStyles,
                        control: (base, state) => ({
                          ...base,
                          minHeight: 38,
                          backgroundColor:
                            theme.palette.checklist.surface.primary,
                          borderColor:
                            errors.priority && touched.priority
                              ? theme.palette.error.main
                              : state.isFocused
                                ? theme.palette.primary.main
                                : theme.palette.checklist.border.subtle,
                        }),
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      required
                    />
                    {errors.priority && touched.priority && (
                      <FormHelperText error sx={{ mt: 0.5 }}>
                        {getFieldError("priority", formData.priority)}
                      </FormHelperText>
                    )}
                  </Grid>

                  {/* Description */}
                  <Grid size={12}>
                    <FormLabel
                      sx={{
                        color: theme.palette.text.primary,
                        mb: 0.75,
                        display: "block",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        "&::after": {
                          content: '"*"',
                          color: theme.palette.error.main,
                          ml: 0.5,
                        },
                      }}
                    >
                      {t("observationForm.descriptionLabel")}
                    </FormLabel>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mb={0.75}
                    >
                      {t("observationForm.descriptionHint")} (
                      {formData.description?.length || 0}/500)
                    </Typography>
                    <TextField
                      multiline
                      rows={3}
                      fullWidth
                      value={formData.description}
                      onChange={handleTextChange("description")}
                      onBlur={handleBlur("description")}
                      required
                      variant="outlined"
                      placeholder={t("observationForm.descriptionPlaceholder")}
                      sx={textFieldStyles}
                      error={errors.description && touched.description}
                      slotProps={{ input: { maxLength: 500 } }}
                    />
                    {errors.description && touched.description && (
                      <FormHelperText error sx={{ mt: 0.5 }}>
                        {getFieldError("description", formData.description)}
                      </FormHelperText>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* File Attachments Section */}
            <Card
              elevation={1}
              sx={{
                mb: 3,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Box
                sx={{
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  px: 2.5,
                  py: 2,
                  bgcolor: (theme) => alpha(theme.palette.warning.main, 0.03),
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: "warning.main",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    fontSize: "1rem",
                  }}
                >
                  <AttachmentIcon sx={{ mr: 1.5, fontSize: 20 }} />
                  {t("observationForm.attachmentsTitle")}
                  <Typography
                    variant="caption"
                    sx={{
                      ml: "auto",
                      color: "text.secondary",
                      fontWeight: 400,
                    }}
                  >
                    {t(
                      "observationForm.attachmentsDescription",
                      "Optional - Images or PDFs"
                    )}
                  </Typography>
                </Typography>
              </Box>
              <CardContent sx={{ p: 2.5 }}>
                {/* Compact File Upload Area */}
                <Box
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  sx={{
                    border: `2px dashed`,
                    borderColor: dragActive ? "primary.main" : "divider",
                    borderRadius: 2,
                    p: 3,
                    bgcolor: dragActive
                      ? (theme) => alpha(theme.palette.primary.main, 0.08)
                      : "action.hover",
                    transition: "all 0.2s ease-in-out",
                    textAlign: "center",
                    cursor: "pointer",
                    "&:hover": {
                      borderColor: "primary.main",
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  <CloudUploadIcon
                    sx={{
                      fontSize: 40,
                      color: dragActive ? "primary.main" : "text.secondary",
                      mb: 1,
                      opacity: dragActive ? 1 : 0.7,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      mb: 0.5,
                    }}
                  >
                    {t("observationForm.dropFilesHere")}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      mb: 2,
                      display: "block",
                    }}
                  >
                    {t("observationForm.dragDropHint")}
                  </Typography>
                  <Button
                    variant="contained"
                    component="label"
                    size="small"
                    startIcon={<CloudUploadIcon />}
                    sx={{
                      borderRadius: 1.5,
                      textTransform: "none",
                      fontWeight: 600,
                      px: 2.5,
                      py: 0.75,
                    }}
                  >
                    {t("observationForm.chooseFiles")}
                    <input
                      type="file"
                      hidden
                      multiple
                      accept="image/*,application/pdf"
                      onChange={handleFileChange}
                    />
                  </Button>
                </Box>

                {/* File List */}
                {selectedFiles.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        mb: 1,
                        display: "block",
                      }}
                    >
                      {t("observationForm.selectedFiles")} (
                      {selectedFiles.length})
                    </Typography>
                    <Stack spacing={0.75}>
                      {selectedFiles.map((file, index) => (
                        <Chip
                          key={index}
                          label={`${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`}
                          onDelete={() => removeFile(index)}
                          deleteIcon={<DeleteIcon />}
                          size="small"
                          variant="outlined"
                          sx={{
                            justifyContent: "space-between",
                            borderColor: theme.palette.checklist.border.subtle,
                            bgcolor: theme.palette.checklist.surface.elevated,
                            "& .MuiChip-label": {
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: "300px",
                            },
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Dynamic Fields Section */}
            <Collapse in={dynamicFields.length > 0}>
              <Card
                elevation={1}
                sx={{
                  mb: 3,
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                <Box
                  sx={{
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    px: 2.5,
                    py: 2,
                    bgcolor: (theme) => alpha(theme.palette.success.main, 0.03),
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: "success.main",
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      fontSize: "1rem",
                    }}
                  >
                    <InfoIcon sx={{ mr: 1.5, fontSize: 20 }} />
                    {t("observationForm.additionalInfoTitle")}
                  </Typography>
                </Box>
                <CardContent sx={{ p: 2.5 }}>
                  <StepInputsForm
                    setFileInputValues={setFileInputValuesStable}
                    inputValues={inputValues}
                    fileInputValues={fileInputValues}
                    setInputValues={setInputValuesStable}
                    stepInputs={dynamicFields}
                    onChange={handleDynamicChange}
                    userList={UsersList}
                  />
                </CardContent>
              </Card>
            </Collapse>

            {/* Submit Section */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
                pt: 2,
                pb: 4,
              }}
            >
              <Button
                variant="outlined"
                color="primary"
                onClick={() => navigate("/myobservations")}
                sx={{
                  borderRadius: 1.5,
                  textTransform: "none",
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                  fontSize: "0.875rem",
                }}
              >
                {t("observationForm.cancelButton")}
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={!isFormValid || loading}
                startIcon={
                  loading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : null
                }
                sx={{
                  borderRadius: 1.5,
                  px: 4,
                  py: 1,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  boxShadow: 2,
                  "&:hover": {
                    boxShadow: 4,
                  },
                  "&:disabled": {
                    opacity: 0.6,
                  },
                }}
              >
                {loading
                  ? t("observationForm.submitting")
                  : t("observationForm.submitButton")}
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* Toast Notification */}
      <Alert
        severity={toast.severity}
        sx={{
          position: "fixed",
          bottom: 16,
          right: 16,
          zIndex: 2000,
          display: toast.open ? "flex" : "none",
          borderRadius: 2,
          boxShadow: 8,
        }}
        onClose={handleToastClose}
      >
        {toast.message}
      </Alert>

      {/* Observation Guide Dialog */}
      <ObservationGuideDialog
        open={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
      />
    </Box>
  );
};

export default ObservationForm;
